import React, { useCallback, useContext } from 'react';
import { first, result, noop, isEmpty } from 'underscore';
import PropTypes from 'prop-types';
import { Label } from '@fluentui/react-components';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { UrlInputFluent } from '@bingads-webui-campaign-react/url-input-fluent-v2';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

const FinalURlKey = 'FinalUrls';

export const ValidatedFinalUrl = ({
  i18n,
  validationOnUrlChange,
  updateValidation,
  prepopulateUrl,
  validationProps,
  validateOnRender,
  helpTopicId,
  hideLabel,
  additionalProperties,
  customUrlProtocol,
}) => {
  const { data } = validationProps;
  const finalUrls = result(data, FinalURlKey, undefined);
  const finalUrl = first(finalUrls);
  const createFinalUrlsViewModel = urls => ({ [FinalURlKey]: urls });

  const activity: any = useContext(ActivityContext);

  const onChange = useCallback((errors, value) => {
    updateValidation(errors, createFinalUrlsViewModel([value]), (!isEmpty(errors) && !!finalUrl));

    if (activity) {
      activity.signal(`onFinalUrlChanged - ${JSON.stringify(createFinalUrlsViewModel([value]))}`);
    }
  }, [activity, finalUrl, updateValidation]);

  return (
    <React.Fragment>
      {!hideLabel &&
        <Label>
          <div>
            {i18n.getString(_TL_('Final URL'))}
            {helpTopicId && <HelpPopupInline topic={helpTopicId} />}
          </div>
        </Label>
      }
      <UrlInputFluent
        url={finalUrl}
        onChange={onChange}
        i18n={i18n}
        prepopulateUrl={prepopulateUrl}
        additionalValidation={validationOnUrlChange}
        dropdownOptionAriaLabel={i18n.getString(_TL_('Final URL options'))}
        propertyKey={FinalURlKey}
        validateOnRender={validateOnRender}
        containerClassName="final-url"
        additionalProperties={additionalProperties}
        textFieldAriaLabel={i18n.getString(_TL_('Final URL'))}
        customUrlProtocol={customUrlProtocol}
      />
    </React.Fragment>
  );
};

ValidatedFinalUrl.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationOnUrlChange: PropTypes.func,
  prepopulateUrl: PropTypes.string,
  hideLabel: PropTypes.bool,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      FinalUrls: PropTypes.arrayOf(PropTypes.string),
    }),
    updateOnMount: PropTypes.bool,
  }).isRequired,
  validateOnRender: PropTypes.bool,
  helpTopicId: PropTypes.string,
  additionalProperties: PropTypes.shape({}),
  customUrlProtocol: PropTypes.bool,
};

ValidatedFinalUrl.defaultProps = {
  validationOnUrlChange: noop,
  prepopulateUrl: '',
  hideLabel: false,
  validateOnRender: false,
  helpTopicId: 'pop_BA_NewAdCreate_FinalURL',
  additionalProperties: {},
  customUrlProtocol: false,
};
